import React from 'react'
import { render, unmountComponentAtNode } from 'react-dom'

import extractStringNodes from '../../utils/extract-string-nodes'
import ownerDocument from '../../utils/owner-document'
import TextFormatter from '../text-formatter'
import { text2HTML } from '../text-formatter/utils'

/* eslint-disable @typescript-eslint/no-explicit-any */
type MeasureResult = {
  finished: boolean
  reactNode: React.ReactNode
}
type Option = {
  rows: number
  suffix?: string
}

// We only handle element & text node.
const ELEMENT_NODE = 1
const TEXT_NODE = 3
const COMMENT_NODE = 8

export const getGhostContainer = (id: string): HTMLElement => {
  {
    const identifier = `ellipsis-ghost-container-${id}`
    const document = ownerDocument()
    let container = document.getElementById(identifier)
    if (!container) {
      container = document.createElement('div')
      container.setAttribute('id', identifier)
      container.setAttribute('aria-hidden', 'true')
      document.body.appendChild(container)
    }
    return container
  }
}

export function removeGhostContainer(id: string): void {
  const container = getGhostContainer(id)
  const document = ownerDocument()
  if (document && container) {
    document.body.removeChild(container)
  }
}

function pxToNumber(value: string | null): number {
  if (!value) return 0

  const match = value.match(/^\d*(\.\d*)?/)

  return match ? Number(match[0]) : 0
}

function styleToString(style: CSSStyleDeclaration) {
  // There are some different behavior between Firefox & Chrome.
  // We have to handle this ourself.
  const styleNames: string[] = Array.prototype.slice.apply(style)
  return styleNames
    .map(name => `${name}: ${style.getPropertyValue(name)};`)
    .join('')
}

export default (
  id: string,
  wrapperElement: HTMLElement,
  option: Option,
  content: React.ReactNode,
  fixedContent: React.ReactNode[] | null,
  ellipsisStr: string
): { content: React.ReactNode; text: string; ellipsis: boolean } => {
  const { rows, suffix = '' } = option
  const contentList: React.ReactNode[] = extractStringNodes(
    React.Children.toArray(content)
  )
  const originalContent = `${contentList}${suffix}`
  const ghostContainer = getGhostContainer(id)

  const childElement =
    wrapperElement?.childNodes[0] &&
    wrapperElement.childNodes[0].nodeType === ELEMENT_NODE
      ? (wrapperElement.childNodes[0] as Element)
      : undefined
  const paragraphElement =
    childElement &&
    childElement.childNodes[0] &&
    childElement.childNodes[0].nodeType === ELEMENT_NODE
      ? (childElement.childNodes[0] as Element)
      : undefined
  const paragraphClassName = paragraphElement ? paragraphElement.className : ''
  const childElementClassName = childElement ? childElement.className : ''
  const childElementStyle = childElement
    ? window.getComputedStyle(childElement)
    : undefined
  // Get origin style
  const wrapperElementStyle = window.getComputedStyle(wrapperElement)
  const originCSS = styleToString(wrapperElementStyle)
  const lineHeight = childElementStyle
    ? pxToNumber(childElementStyle.fontSize)
    : pxToNumber(wrapperElementStyle.fontSize)
  const maxHeight = Math.round(
    lineHeight * (rows + 1) +
      pxToNumber(wrapperElementStyle.paddingTop) +
      pxToNumber(wrapperElementStyle.paddingBottom)
  )

  // Set shadow
  ghostContainer.setAttribute('style', originCSS)
  ghostContainer.style.position = 'fixed'
  ghostContainer.style.left = '0'
  ghostContainer.style.height = 'auto'
  ghostContainer.style.minHeight = 'auto'
  ghostContainer.style.maxHeight = 'auto'
  ghostContainer.style.top = '-999999px'
  ghostContainer.style.zIndex = '-1000'

  // clean up css overflow
  ghostContainer.style.textOverflow = 'clip'
  ghostContainer.style.whiteSpace = 'normal'
  ghostContainer.style.webkitLineClamp = 'none'

  // Render in the fake container
  render(
    <div>
      <span key={`ghost-container-${id}`} className={childElementClassName}>
        <TextFormatter id={`ghost-container-section-${id}`}>
          {originalContent}
        </TextFormatter>
      </span>
      <span className={childElementClassName}>{fixedContent}</span>
    </div>,
    ghostContainer
  ) // wrap in an div for old version react

  // Check if ellipsis in measure div is height enough for content
  function inRange() {
    return ghostContainer.offsetHeight < maxHeight
  }

  // Skip ellipsis if already match
  if (inRange()) {
    unmountComponentAtNode(ghostContainer)
    return { content, text: ghostContainer.innerHTML, ellipsis: false }
  }

  // We should clone the childNode since they're controlled by React and we can't reuse it without warning
  const childNodes: ChildNode[] = Array.prototype.slice
    .apply(
      ghostContainer.childNodes[0].childNodes[0].cloneNode(true).childNodes
    )
    .filter(({ nodeType }: ChildNode) => nodeType !== COMMENT_NODE)

  const fixedNodes: ChildNode[] = Array.prototype.slice.apply(
    ghostContainer.childNodes[0].childNodes[1].cloneNode(true).childNodes
  )
  unmountComponentAtNode(ghostContainer)

  // ========================= Find match ellipsis content =========================
  const ellipsisChildren: React.ReactNode[] = []
  ghostContainer.innerHTML = ''

  // Create origin content holder
  const ellipsisContentHolder = document.createElement('span')
  ellipsisContentHolder.className = childElementClassName
  ghostContainer.appendChild(ellipsisContentHolder)
  const ellipsisTextNode = document.createTextNode(ellipsisStr + suffix)
  ellipsisContentHolder.appendChild(ellipsisTextNode)

  fixedNodes.forEach(childNode => {
    ghostContainer.appendChild(childNode)
  })

  // Append before fixed nodes
  function appendChildNode(node: ChildNode) {
    ellipsisContentHolder.insertBefore(node, ellipsisTextNode)
  }

  let appendingHtml = ''

  function getAppendingHTML(parentElement: HTMLElement) {
    if (parentElement?.nextElementSibling) {
      const nextElementSibling = parentElement?.nextElementSibling
      appendingHtml = parentElement?.nextElementSibling?.outerHTML
      nextElementSibling && nextElementSibling.remove()
    }
    return appendingHtml
  }

  // Get maximum text
  function measureText(
    parentElement: HTMLElement,
    fullText: string,
    startLoc = 0,
    endLoc = fullText.length,
    lastSuccessLoc = 0
  ): MeasureResult {
    const midLoc = Math.floor((startLoc + endLoc) / 2)
    const currentText = fullText.slice(0, midLoc)
    const appendingHTML = getAppendingHTML(parentElement)
    parentElement.innerHTML = text2HTML(
      currentText + ellipsisStr + suffix,
      paragraphClassName,
      appendingHTML
    )

    if (startLoc >= endLoc - 1) {
      // Loop when step is small
      for (let step = endLoc; step >= startLoc; step -= 1) {
        const currentStepText = fullText.slice(0, step)
        parentElement.innerHTML = text2HTML(
          currentStepText + ellipsisStr + suffix,
          paragraphClassName,
          appendingHTML
        )

        if (inRange() || !currentStepText) {
          return step === fullText.length
            ? {
                finished: false,
                reactNode: fullText
              }
            : {
                finished: true,
                reactNode: currentStepText
              }
        }
      }
    }

    if (inRange()) {
      return measureText(parentElement, fullText, midLoc, endLoc, midLoc)
    }
    return measureText(
      parentElement,
      fullText,
      startLoc,
      midLoc,
      lastSuccessLoc
    )
  }

  function measureNode(childNode: ChildNode, index: number): MeasureResult {
    const type = childNode.nodeType

    if (type === ELEMENT_NODE) {
      // We don't split element, it will keep if whole element can be displayed.
      appendChildNode(childNode)
      if (inRange()) {
        return {
          finished: false,
          reactNode: contentList[index]
        }
      }

      // Clean up if can not pull in
      ellipsisContentHolder.removeChild(childNode)
      // resolve measureText using the originalContent
      return measureText(ellipsisContentHolder, originalContent)
    }
    if (type === TEXT_NODE) {
      const fullText = childNode.textContent || ''
      const textNode = document.createTextNode(fullText)
      appendChildNode(textNode)
      return measureText(textNode.parentElement as HTMLElement, fullText)
    }

    // Not handle other type of content
    return {
      finished: false,
      reactNode: null
    }
  }

  childNodes.some((childNode, index) => {
    const { reactNode, finished } = measureNode(childNode, index)
    if (reactNode) {
      ellipsisChildren.push(reactNode)
    }
    return finished
  })

  return {
    content: ellipsisChildren,
    text: ghostContainer.innerHTML,
    ellipsis: true
  }
}
